import type { SVGProps } from "react";

/** Fairway Line, A3. Original 24-unit SVG drawings. One meaning per silhouette;
 * no ornamental strokes or borrowed library paths. See ICONS.md for the audit. */
type IconProps = Omit<SVGProps<SVGSVGElement>, "name"> & {
  size?: number | string;
};
const drawings = {
  ArrowRight: ["M4 12h15m-6-6 6 6-6 6"],
  ArrowLeft: ["M20 12H5m6-6-6 6 6 6"],
  ArrowUpRight: ["M5 19 19 5M8 5h11v11"],
  ChevronRight: ["m9 5 7 7-7 7"],
  ChevronLeft: ["m15 5-7 7 7 7"],
  ChevronDown: ["m5 9 7 7 7-7"],
  Check: ["m4 12 5 5L20 6"],
  X: ["m5 5 14 14M19 5 5 19"],
  Plus: ["M12 4v16M4 12h16"],
  Minus: ["M4 12h16"],
  Flag: ["M6 21V3m0 1h13l-3 4 3 4H6"],
  House: ["m3 10 9-7 9 7M5 8.5V21h14V8.5M9.5 21v-7h5v7"],
  // Golf shafts cross; each ends in one compact, solid iron head.
  // Kept as Swords to preserve the existing consumer API.
  Swords: [
    "M4 3.5 17 19m3-15.5L7 19",
    "m17 18.3 3.7-.3c.7 0 1.1.4 1.1 1v1.5c0 .6-.4 1-1 1h-5.3c-.7 0-1-.8-.5-1.3l2-1.9Zm-10 0L3.3 18c-.7 0-1.1.4-1.1 1v1.5c0 .6.4 1 1 1h5.3c.7 0 1-.8.5-1.3l-2-1.9Z",
  ],
  Trophy: [
    "M7 3h10v7a5 5 0 0 1-10 0V3ZM7 5H3v3a4 4 0 0 0 4 4m10-7h4v3a4 4 0 0 1-4 4M12 15v6m-4 0h8",
  ],
  Shield: ["M12 2.5 21 6v6c0 4-4.5 7.5-9 10-4.5-2.5-9-6-9-10V6l9-3.5Z"],
  ShieldCheck: [
    "M12 2.5 21 6v6c0 4-4.5 7.5-9 10-4.5-2.5-9-6-9-10V6l9-3.5Z",
    "m7.5 11.5 3 3 6-6",
  ],
  LockKeyhole: [
    "M6 10.5h12a1.5 1.5 0 0 1 1.5 1.5v8a1.5 1.5 0 0 1-1.5 1.5H6A1.5 1.5 0 0 1 4.5 20v-8A1.5 1.5 0 0 1 6 10.5ZM7.5 10.5V7a4.5 4.5 0 0 1 9 0v3.5M12 15v3",
  ],
  Users: [
    "M5.5 7a3.5 3.5 0 1 0 7 0 3.5 3.5 0 0 0-7 0ZM3 21v-2a6 6 0 0 1 12 0v2",
    "M16 3.5a3.5 3.5 0 0 1 0 7M17 13a5.5 5.5 0 0 1 4 5.5V21",
  ],
  UserRound: ["M8 6.5a4 4 0 1 0 8 0 4 4 0 0 0-8 0ZM4 21v-2a8 6 0 0 1 16 0v2"],
  MapPin: [
    "M19.5 10C19.5 15 12 21.3 12 21.3S4.5 15 4.5 10a7.5 7.5 0 0 1 15 0Z",
    "M9.5 10a2.5 2.5 0 1 0 5 0 2.5 2.5 0 0 0-5 0Z",
  ],
  Search: ["M3 10a7 7 0 1 0 14 0 7 7 0 0 0-14 0Zm12 5 6 6"],
  Star: [
    "m12 2.5 3 6.2 6.9 1-5 4.8 1.2 6.8-6.1-3.2-6.1 3.2 1.2-6.8-5-4.8 6.9-1L12 2.5Z",
  ],
  Sparkles: [
    "M10 4c.7 5 2.8 7.5 7 8-4.2.5-6.3 3-7 8-.7-5-2.8-7.5-7-8 4.2-.5 6.3-3 7-8Z",
    "M19 2v6m-3-3h6",
  ],
  Grid3X3: ["M3 3h18v18H3V3Zm0 6h18M3 15h18M9 3v18M15 3v18"],
  History: ["M4 7a9 9 0 1 1-.5 9M3 2.5V8h5.5M12 7v5l3.5 2.5"],
  RotateCcw: ["M4 7a9 9 0 1 1-.5 9M3 2.5V8h5.5"],
  CloudOff: [
    "M8 18H7a4 4 0 0 1-.8-7.9 7 7 0 0 1 .7-2.3M9.5 5a7 7 0 0 1 9 6 3.2 3.2 0 0 1 2.3 5.2M11 18h5.5",
    "M3 3 21 21",
  ],
  CircleCheck: [
    "M2.5 12a9.5 9.5 0 1 0 19 0 9.5 9.5 0 0 0-19 0Z",
    "m7 12 3.5 3.5 6.5-7",
  ],
  Info: ["M2.5 12a9.5 9.5 0 1 0 19 0 9.5 9.5 0 0 0-19 0Z", "M12 11v6m0-10v.1"],
  AlertCircle: [
    "M2.5 12a9.5 9.5 0 1 0 19 0 9.5 9.5 0 0 0-19 0Z",
    "M12 7v6m0 4v.1",
  ],
  MessageCircle: ["M3 4.5h18v13H9l-6 4v-17Z"],
  Share2: ["M12 16V2.5m-5 5 5-5 5 5M7 10H4v11h16V10h-3"],
  Download: ["M12 2.5V16m-5-5 5 5 5-5M3 17v4h18v-4"],
  SlidersHorizontal: [
    "M3 6h4m4 0h10M3 12h10m4 0h4M3 18h4m4 0h10",
    "M7 4v4h4V4H7Zm6 6v4h4v-4h-4Zm-6 6v4h4v-4H7Z",
  ],
  Maximize2: ["M3 9V3h6m6 0h6v6M21 15v6h-6m-6 0H3v-6"],
  Ellipsis: [
    "M3 12a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0ZM10.5 12a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0ZM18 12a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0Z",
  ],
  Settings: [
    "m9.5 2-.5 3-2 .9-2.9-.9-2 3.5 2.4 2v3l-2.4 2 2 3.5 2.9-.9 2 .9.5 3h5l.5-3 2-.9 2.9.9 2-3.5-2.4-2v-3l2.4-2-2-3.5L17 5.9 15 5l-.5-3h-5Z",
    "M8.5 12a3.5 3.5 0 1 0 7 0 3.5 3.5 0 0 0-7 0Z",
  ],
  HelpCircle: [
    "M2.5 12a9.5 9.5 0 1 0 19 0 9.5 9.5 0 0 0-19 0Z",
    "M9 8.5c0-3.7 6-3.7 6 0 0 2.5-3 2.3-3 5m0 3.5v.1",
  ],
  Bell: ["M4 18h16l-2-3V9a6 6 0 0 0-12 0v6l-2 3ZM10 20.5c.7 1.3 3.3 1.3 4 0"],
  Mail: ["M3 5h18v14H3V5Zm0 0 9 8 9-8"],
  Compass: [
    "M2.5 12a9.5 9.5 0 1 0 19 0 9.5 9.5 0 0 0-19 0Z",
    "m16.5 7.5-2.7 6.3-6.3 2.7 2.7-6.3 6.3-2.7Z",
  ],
  BookOpen: [
    "M12 5.5c-3-2-6-2.5-9-2v15c3-.5 6 0 9 2 3-2 6-2.5 9-2v-15c-3-.5-6 0-9 2Zm0 0v15",
  ],
  Monitor: [
    "M2.5 3.5h19v14h-19v-14ZM12 17.5V21m-4 0h8",
    "M9 14V7h7l-2 2.5 2 2.5H9",
  ],
  UserPlus: [
    "M5 6.5a3.5 3.5 0 1 0 7 0 3.5 3.5 0 0 0-7 0ZM3 21v-2a6 6 0 0 1 9-5.5M17 12v9m-4.5-4.5h9",
  ],
  Target: [
    "M12 2.5A9.5 9.5 0 1 0 21.5 12M12 7.5a4.5 4.5 0 1 0 4.5 4.5",
    "M12 12 21 3m-5 0h5v5",
  ],
} satisfies Record<string, string[]>;

export type FairwayIconName = keyof typeof drawings;
export function FairwayIcon({
  name,
  size = 24,
  strokeWidth = 1.65,
  ...props
}: IconProps & { name: FairwayIconName }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-fr-icon={name}
      {...props}
    >
      {drawings[name].map((d, i) => (
        <path
          key={i}
          d={d}
          transform={
            name === "Settings" ? "translate(.5 .5) scale(.958333)" : undefined
          }
          fill={
            name === "Ellipsis" || (name === "Swords" && i === 1)
              ? "currentColor"
              : "none"
          }
          stroke={
            name === "Ellipsis" || (name === "Swords" && i === 1)
              ? "none"
              : undefined
          }
        />
      ))}
    </svg>
  );
}
function icon(name: FairwayIconName) {
  return (props: IconProps) => <FairwayIcon name={name} {...props} />;
}
export const ArrowRight = icon("ArrowRight"),
  ArrowLeft = icon("ArrowLeft"),
  ArrowUpRight = icon("ArrowUpRight"),
  ChevronRight = icon("ChevronRight"),
  ChevronLeft = icon("ChevronLeft"),
  ChevronDown = icon("ChevronDown");
export const Check = icon("Check"),
  X = icon("X"),
  Plus = icon("Plus"),
  Minus = icon("Minus"),
  Flag = icon("Flag"),
  House = icon("House"),
  Swords = icon("Swords"),
  Trophy = icon("Trophy"),
  Shield = icon("Shield"),
  ShieldCheck = icon("ShieldCheck");
export const LockKeyhole = icon("LockKeyhole"),
  Users = icon("Users"),
  UserRound = icon("UserRound"),
  MapPin = icon("MapPin"),
  Search = icon("Search"),
  Star = icon("Star"),
  Sparkles = icon("Sparkles"),
  Grid3X3 = icon("Grid3X3"),
  History = icon("History"),
  RotateCcw = icon("RotateCcw");
export const CloudOff = icon("CloudOff"),
  CircleCheck = icon("CircleCheck"),
  CheckCircle2 = CircleCheck,
  Info = icon("Info"),
  AlertCircle = icon("AlertCircle"),
  MessageCircle = icon("MessageCircle"),
  Share2 = icon("Share2"),
  Download = icon("Download"),
  SlidersHorizontal = icon("SlidersHorizontal"),
  Maximize2 = icon("Maximize2"),
  Ellipsis = icon("Ellipsis");
export const Settings = icon("Settings"),
  HelpCircle = icon("HelpCircle"),
  Bell = icon("Bell"),
  Mail = icon("Mail"),
  Compass = icon("Compass"),
  BookOpen = icon("BookOpen");
export const Monitor = icon("Monitor"),
  UserPlus = icon("UserPlus");
export const Target = icon("Target");
